// The "send an improvement request" form: annotate the screenshot, say what
// should change, pick a type and priority.
import React, { useEffect, useRef, useState } from "react";
import { apiErrorMessage } from "../../api";
import Annotator from "./Annotator";
import { fileToDataUrl, imageFromPaste } from "./captureScreen";
import { CATEGORIES, PRIORITIES, createFeedback } from "./feedbackApi";

const label = { display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 4 };
const input = {
  width: "100%", padding: "8px 10px", border: "1px solid #d1d5db", borderRadius: 8,
  fontSize: 14, fontFamily: "inherit", boxSizing: "border-box",
};

export default function FeedbackModal({ initialImage = null, pageUrl, onClose, onCreated }) {
  const [image, setImage] = useState(initialImage);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("IMPROVEMENT");
  const [priority, setPriority] = useState("MEDIUM");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const annotatorRef = useRef(null);
  const fileRef = useRef(null);

  const takeFile = async (file) => {
    try {
      setImage(await fileToDataUrl(file));
      setError("");
    } catch (e) {
      setError(e.message);
    }
  };

  // Paste a screenshot anywhere in the modal (Cmd+Shift+4, then Cmd+V).
  useEffect(() => {
    const onPaste = (e) => {
      const file = imageFromPaste(e);
      if (file) {
        e.preventDefault();
        takeFile(file);
      }
    };
    const onKey = (e) => e.key === "Escape" && !saving && onClose();
    window.addEventListener("paste", onPaste);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("paste", onPaste);
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose, saving]);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      setError("Give it a short title.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const created = await createFeedback({
        title: title.trim(),
        description: description.trim(),
        category,
        priority,
        page_url: pageUrl || "",
        ...(image ? { image: annotatorRef.current?.getImage() || image } : {}),
      });
      onCreated?.(created);
    } catch (err) {
      setError(apiErrorMessage(err, "Could not send that request."));
      setSaving(false);
    }
  };

  return (
    <div
      data-html2canvas-ignore="true"
      role="dialog"
      aria-modal="true"
      aria-label="New improvement request"
      style={{
        position: "fixed", inset: 0, background: "rgba(17,24,39,0.45)", zIndex: 2000,
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      }}
    >
      <form
        onSubmit={submit}
        style={{
          background: "#fff", borderRadius: 14, width: "min(1180px, 100%)",
          maxHeight: "94vh", overflow: "auto", boxShadow: "0 20px 50px rgba(0,0,0,0.25)",
          display: "grid", gridTemplateColumns: "minmax(0, 1fr) 320px",
        }}
      >
        <div style={{ padding: 20, borderRight: "1px solid #f3f4f6" }}>
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 10 }}>
            {image ? "Mark up what you mean" : "Add a screenshot"}
          </div>
          {image ? (
            <>
              <Annotator ref={annotatorRef} src={image} />
              <button type="button" onClick={() => setImage(null)}
                style={{ marginTop: 8, background: "none", border: "none", color: "#6b7280", cursor: "pointer", fontSize: 12 }}>
                Remove screenshot
              </button>
            </>
          ) : (
            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const file = e.dataTransfer.files?.[0];
                if (file) takeFile(file);
              }}
              style={{
                border: "2px dashed #d1d5db", borderRadius: 10, padding: "48px 16px",
                textAlign: "center", color: "#6b7280", fontSize: 14,
              }}
            >
              Paste a screenshot (⌘V), drop an image here, or{" "}
              <button type="button" onClick={() => fileRef.current?.click()}
                style={{ background: "none", border: "none", color: "#4f46e5", cursor: "pointer", fontSize: 14, padding: 0 }}>
                choose a file
              </button>
              .
              <div style={{ fontSize: 12, marginTop: 8 }}>
                Tip: the 💬 Feedback button in the corner captures the page you're on.
              </div>
              <input ref={fileRef} type="file" accept="image/*" hidden
                onChange={(e) => e.target.files?.[0] && takeFile(e.target.files[0])} />
            </div>
          )}
        </div>

        <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <label style={label} htmlFor="fb-title">What should change?</label>
            <input id="fb-title" style={input} value={title} autoFocus maxLength={200}
              placeholder="e.g. Buttons on the invoice screen don't line up"
              onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div>
            <label style={label} htmlFor="fb-desc">Details</label>
            <textarea id="fb-desc" style={{ ...input, minHeight: 110, resize: "vertical" }}
              value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="What did you expect? What would make it better? Refer to pins by number." />
          </div>
          <div>
            <span style={label}>Type</span>
            <div style={{ display: "grid", gap: 6 }}>
              {CATEGORIES.map((c) => (
                <label key={c.value} style={{
                  display: "flex", gap: 8, alignItems: "flex-start", cursor: "pointer",
                  border: "1px solid " + (category === c.value ? c.color : "#e5e7eb"),
                  background: category === c.value ? c.bg : "#fff",
                  borderRadius: 8, padding: "6px 10px", fontSize: 13,
                }}>
                  <input type="radio" name="fb-category" value={c.value}
                    checked={category === c.value} onChange={() => setCategory(c.value)} />
                  <span><strong>{c.label}</strong> <span style={{ color: "#6b7280" }}>· {c.hint}</span></span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <span style={label}>Priority</span>
            <div style={{ display: "flex", gap: 6 }}>
              {PRIORITIES.map((p) => (
                <button key={p.value} type="button" aria-pressed={priority === p.value}
                  onClick={() => setPriority(p.value)}
                  style={{
                    flex: 1, padding: "6px 0", borderRadius: 8, cursor: "pointer", fontSize: 13,
                    border: "1px solid " + (priority === p.value ? p.color : "#e5e7eb"),
                    color: priority === p.value ? p.color : "#374151",
                    background: "#fff", fontWeight: priority === p.value ? 700 : 500,
                  }}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          {pageUrl && (
            <div style={{ fontSize: 12, color: "#6b7280" }}>Page: <code>{pageUrl}</code></div>
          )}
          {error && <div role="alert" style={{ color: "#b91c1c", fontSize: 13 }}>{error}</div>}
          <div style={{ marginTop: "auto", display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button type="button" onClick={onClose} disabled={saving}
              style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #d1d5db", background: "#fff", cursor: "pointer" }}>
              Cancel
            </button>
            <button type="submit" disabled={saving}
              style={{ padding: "8px 16px", borderRadius: 8, border: "none", background: "#4f46e5", color: "#fff", fontWeight: 600, cursor: "pointer" }}>
              {saving ? "Sending…" : "Send request"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
